import PropTypes from "prop-types";
import React, { memo, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";

import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableFooter from "@mui/material/TableFooter";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";

import { makeStyles } from "tss-react/mui";

// For virtulization
import { TableVirtuoso } from "react-virtuoso";

const useStyles = makeStyles()((theme) => ({
  BrowserTableContainer: {
    maxHeight: "100%",
    minHeight: "100%",
    "& .MuiTableCell-head": {
      fontWeight: "600 !important",
      backgroundColor: "#e8e8e8",
    },
  },
  NoResultContainer: {
    paddingTop: "10px",
    minHeight: "100%",
    height: "100%",
  },
  TablePaper: {
    maxWidth: "100%",
    minHeight: "calc(100% - 45px)",
    height: "calc(100% - 45px)",
    overflow: "hidden",
  },
  RowCell: {
    width: "41px",
    padding: 0,
    textAlign: "center",
    backgroundColor: "#e8e8e8",
  },
  unifiedCell: {
    whiteSpace: "nowrap",
    width: "auto",
    maxWidth: "1000px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    borderRight: "1px solid #D1D5DB",
  },
}));

const fixedHeaderContent = (queryResultColumns, classes) => {
  return (
    <TableRow>
      <TableCell style={{ width: "41px", position: "sticky", left: 0, zIndex: 1000 }} component="th"></TableCell>
      {queryResultColumns.map((column) => (
        <TableCell
          key={column.name}
          // className={classes.RowCell}
          align={column.align}
          style={{
            minWidth: column.minWidth,
          }}
          className={classes.unifiedCell}
          component="th"
        >
          {column.name}
        </TableCell>
      ))}
    </TableRow>
  );
};

const fixedFooterContent = (queryResultColumns, classes, loading) => {
  if (!loading) return <></>;

  return (
    <TableRow sx={{ backgroundColor: "#ffffff" }}>
      <TableCell className={classes.RowCell} sx={{ position: "sticky", left: 0 }}>
        ...
      </TableCell>
      {queryResultColumns.map((column) => (
        <TableCell
          key={column.name}
          align={column.align}
          style={{
            minWidth: column.minWidth,
          }}
          className={classes.unifiedCell}
        >
          Loading...
        </TableCell>
      ))}
    </TableRow>
  );
};

const CreateNoDataText = () => {
  const workspace = useSelector((state) => state.workspace);
  const { isOpenAIIntegrated } = workspace;
  const showOpenAIMessage = useMemo(() => window.location.href.includes("data-explorer"), []);

  return (
    <>
      <span>Query results will appear here.</span>
      {isOpenAIIntegrated && showOpenAIMessage && (
        <>
          <br />
          <span>
            Type <code>`USE DB_NAME.SCHEMA_NAME`</code> and <code>`-- your instruction`</code> +{" "}
            <button
              style={{
                cursor: "text",
              }}
            >
              CTRL + ENTER
            </button>
            (keep cursor within your instruction) to try out AI-Generated SQL queries
          </span>
        </>
      )}
    </>
  );
};

const ColGroup = ({ columns }) => (
  <colgroup>
    <col style={{ width: "41px" }} />
    {columns.map((c) => (
      <col key={c.name} style={{ width: `${c.minWidth}px` }} />
    ))}
  </colgroup>
);

const DataTable = memo(({ queryResultColumns, queryResult, queryError, height, loading, fetchMoreData }) => {
  const { classes } = useStyles();

  const VirtuosoTableComponents = {
    Table: ({ children, ...props }) => (
      <Table {...props} stickyHeader size="small">
        <ColGroup columns={queryResultColumns} />
        {children}
      </Table>
    ),
    TableRow: ({ item: _item, ...props }) => <TableRow {...props} />,
    TableBody: React.forwardRef((props, ref) => <TableBody {...props} ref={ref} />),
    TableCell: (props) => <TableCell {...props} />,
    TableHead: (props) => <TableHead {...props} className={classes.BrowserTableContainer} />,
    TableFooter: (props) => <TableFooter {...props} />,
  };

  const rowContent = useCallback(
    (_index, row) => {
      return (
        <>
          <TableCell key={_index} className={classes.RowCell} sx={{ position: "sticky", left: 0 }}>
            {_index + 1}
          </TableCell>
          {queryResultColumns.map((column) => (
            <TableCell key={column.name} className={classes.unifiedCell}>
              {typeof row[column.name] === "boolean" ? (row[column.name] ? "TRUE" : "FALSE") : row[column.name]}
            </TableCell>
          ))}
        </>
      );
    },
    [classes.RowCell, classes.unifiedCell, queryResultColumns]
  );

  return (
    <Paper elevation={0} className={classes.TablePaper} style={height ? { height: height } : {}}>
      {queryResultColumns.length ? (
        <TableVirtuoso
          data={queryResult}
          components={VirtuosoTableComponents}
          fixedFooterContent={() => fixedFooterContent(queryResultColumns, classes, loading)}
          fixedHeaderContent={() => fixedHeaderContent(queryResultColumns, classes)}
          itemContent={(_index, row) => rowContent(_index, row)}
          endReached={fetchMoreData}
        />
      ) : (
        <Box
          className={classes.NoResultContainer}
          sx={{
            overflowY: "auto",
            ...(queryError
              ? {
                  whiteSpace: "break-spaces",
                  color: (theme) => theme.palette.error.main,
                  paddingLeft: "10px",
                }
              : {
                  display: "flex",
                  justifyContent: "center",
                  alignContent: "center",
                  alignItems: "center",
                }),
          }}
        >
          <div
            style={
              queryError
                ? {}
                : {
                    textAlign: "center",
                  }
            }
          >
            {queryError?.length > 0 ? queryError : <CreateNoDataText />}
          </div>
        </Box>
      )}
    </Paper>
  );
});

DataTable.propTypes = {
  queryResultColumns: PropTypes.array,
  queryResult: PropTypes.array,
  queryError: PropTypes.string,
  height: PropTypes.string,
  loading: PropTypes.bool,
  fetchMoreData: PropTypes.array,
};

export default memo(DataTable);
